import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import supabase from '../../supabaseClient'
import Loader from '../Loader'
import SignupLayout from './SignupLayout'
import { readDraft, writeDraft } from './signupState'
import {
  academyLoginRedirectState,
  fetchExistingAcademyMembership,
} from '../../utils/existingAcademyCheck'
import {
  communityLoginRedirectState,
  fetchExistingCommunityMembership,
  shouldAllowCommunitySignupFlow,
} from '../../utils/existingCommunityCheck'
import {
  fetchExistingVenueMembership,
  venueOwnerLoginRedirectState,
} from '../../utils/existingVenueCheck'
import { clearSignupSkipAutoRedirect } from '../../utils/signOutSignupFlow'
import { consumeQuickUploadAuth, markQuickUploadOpen } from '../../lib/quickUpload'

function authMethodFromUser(user) {
  const provider =
    user?.app_metadata?.provider ||
    user?.identities?.[0]?.provider ||
    'google'
  if (provider === 'azure') return 'microsoft'
  if (provider === 'google') return 'google'
  return provider
}

function profileFromUser(user) {
  const fullName =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    ''
  const email = user?.email || ''
  return { fullName, email, authMethod: authMethodFromUser(user) }
}

function nextRouteForPersona(persona) {
  if (persona === 'community') return '/upload'
  if (persona === 'academy') return '/signup/academy/book'
  if (persona === 'venue') return '/signup/venue/book'
  return '/signup'
}

/**
 * OAuth return handler for the /signup funnel. Reads the Supabase
 * session, stores email + name on the signup draft, then continues
 * to the persona-specific intake step.
 */
export default function SignupCallback() {
  const navigate = useNavigate()

  useEffect(() => {
    let settled = false

    const resumeSignup = async (session) => {
      if (settled || !session?.user) return
      settled = true
      clearSignupSkipAutoRedirect()

      if (consumeQuickUploadAuth()) {
        markQuickUploadOpen()
        navigate('/?upload=1', { replace: true })
        return
      }

      const draft = readDraft()
      const persona = draft.persona
      if (!persona) {
        navigate('/signup', { replace: true })
        return
      }

      if (persona === 'community') {
        const membership = await fetchExistingCommunityMembership(session.user.id)
        if (
          membership &&
          !shouldAllowCommunitySignupFlow(membership.communityId)
        ) {
          navigate('/community/login', {
            replace: true,
            state: communityLoginRedirectState(membership.communityName),
          })
          return
        }
      }

      if (persona === 'academy') {
        const membership = await fetchExistingAcademyMembership(session.user.id)
        if (membership) {
          navigate('/academy/login', {
            replace: true,
            state: academyLoginRedirectState(membership.academyName),
          })
          return
        }
      }

      if (persona === 'venue') {
        const membership = await fetchExistingVenueMembership(session.user.id)
        if (membership) {
          navigate('/venue-owner/login', {
            replace: true,
            state: venueOwnerLoginRedirectState(membership.venueName),
          })
          return
        }
      }

      const { fullName, email, authMethod } = profileFromUser(session.user)
      writeDraft({
        authMethod,
        email,
        fullName,
        signupStep: 1,
      })
      navigate(nextRouteForPersona(persona), { replace: true })
    }

    const fail = (message) => {
      if (settled) return
      settled = true
      const quick = consumeQuickUploadAuth()
      if (quick) markQuickUploadOpen()
      navigate(quick ? '/?upload=1' : '/signup/account', {
        replace: true,
        state: {
          oauthError:
            message ||
            'Sign-in did not complete. Please try Google or Microsoft again.',
        },
      })
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (
          (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') &&
          session
        ) {
          resumeSignup(session)
        }
      },
    )

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) resumeSignup(session)
    })

    const timeout = window.setTimeout(() => {
      if (!settled) {
        fail('Sign-in timed out. Please try again.')
      }
    }, 12000)

    return () => {
      settled = true
      window.clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [navigate])

  return (
    <SignupLayout showLogin={false}>
      <div className="flex flex-col items-center justify-center pt-12">
        <Loader size={32} className="text-[var(--signup-bg-base)]" />
        <p className="text-[14px] text-[var(--signup-text-secondary)] mt-5">
          Setting up your account…
        </p>
      </div>
    </SignupLayout>
  )
}
